import React, { useMemo } from 'react'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { SxProps, Theme } from '@mui/material/styles'

import ShiftDashboardCard from '../components/ShiftDashboardCard'
import { useGetMyShiftsQuery } from '@/core/services/shift-marketplace'

dayjs.extend(utc)

type ShiftDashboardCardContainerProps = {
    sx?: SxProps<Theme>
}

export default function ShiftDashboardCardContainer({ sx }: ShiftDashboardCardContainerProps) {
    // Get current date and set up date range for next 7 days
    const today = dayjs.utc().startOf('day')
    const nextWeek = today.add(7, 'day')

    // Fetch user's shifts for the next 7 days
    const { data, isLoading, error } = useGetMyShiftsQuery({
        startDate: today.toISOString(),
        endDate: nextWeek.toISOString(),
        limit: 10
    }, {
        // This ensures the component always shows fresh data
        refetchOnMountOrArgChange: true
    })

    // Process and sort the shifts
    const upcomingShifts = useMemo(() => {
        if (!data?.items) return []

        // Filter to only include upcoming shifts and sort by start time
        return [...data.items]
            .filter(shift => {
                const shiftStartDate = dayjs.utc(shift.startTime)
                // Include shifts that start today or in the future
                return !shiftStartDate.isBefore(today, 'day')
            })
            .sort((a, b) => {
                const dateA = dayjs.utc(a.startTime)
                const dateB = dayjs.utc(b.startTime)
                return dateA.valueOf() - dateB.valueOf()
            })
            .slice(0, 3) // Only show the next 3 shifts
    }, [data, today])

    return (
        <ShiftDashboardCard
            upcomingShifts={upcomingShifts}
            isLoading={isLoading}
            error={error}
            sx={sx}
        />
    )
}
